import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { masterAuth } from '../../firebase/master';
import { useAuth } from '../../context/AuthContext';
import { MfaCodeStep } from '../../components/MfaCodeStep';
import { isMfaRequired, startSignInMfa, mfaErrorText, type MfaChallenge } from '../../utils/mfa';

export default function AdminLogin() {
  const { isAdmin, isLoading, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mfa, setMfa] = useState<MfaChallenge | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (isAdmin) navigate('/admin/panel', { replace: true });
    if (user) navigate('/dashboard', { replace: true });
  }, [isAdmin, user, isLoading, navigate]);

  // AuthContext checks whether this user is in /admins and redirects; if
  // we're still here after 3s it wasn't an admin account.
  function watchForAdmin() {
    setTimeout(() => {
      setSubmitting(false);
      setMfa(null);
      setError('This account does not have admin access.');
    }, 3000);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signInWithEmailAndPassword(masterAuth, email, password);
      watchForAdmin();
    } catch (err: unknown) {
      if (isMfaRequired(err)) {
        try {
          setMfa(startSignInMfa(err));
        } catch (e) {
          setError(mfaErrorText(e));
        }
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      const code = (err as { code?: string }).code;
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found' ||
        code === 'auth/invalid-email'
      ) {
        setError('Invalid email or password.');
      } else {
        setError('Login failed. Please try again.');
      }
    }
  }

  if (isLoading) {
    return (
      <div className="loading-page">
        <span className="spinner" />
        Loading…
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L3 8h14L10 2z" fill="white" opacity=".9" />
              <path d="M3 8l7 10 7-10H3z" fill="white" />
            </svg>
          </div>
          <div>
            <h1>Admin Panel</h1>
            <p className="subtitle">DiaWolt — Super Admin</p>
          </div>
        </div>

        {mfa ? (
          <MfaCodeStep
            challenge={mfa}
            onCancel={() => { setMfa(null); setPassword(''); }}
            onSignedIn={watchForAdmin}
          />
        ) : (<>
        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Admin Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="admin@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={submitting} style={{ marginTop: 4 }}>
            {submitting ? (
              <>
                <span className="spinner" style={{ borderTopColor: 'white', marginRight: 8 }} />
                Signing in…
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
        </>)}
      </div>
    </div>
  );
}
